import { Fragment, memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { InfiniteScroll, NoResults } from 'basic-components';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import { Fetching, globalActions } from 'primary-components';

import getApiConfig from 'api/ApiConfig';

import Row from './Row';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { When } from 'react-if';

import './Body.scss';

const REQUEST_ID = "History_Statuses"
const useSearchStatuses = ({ name, type, getQueryBuilder, shouldBeLimited }) =>
  useInfiniteQuery({
    ...StatusesQuery.QUERIES.search(
      getQueryBuilder, 
      { 
        query: type === StatusesEnums.STATUS_TYPE.ETL ? '*' : name, 
        groupName: type === StatusesEnums.STATUS_TYPE.ETL ? name : undefined, 
        size: CommonService.getRequestSize(32), 
        REQUEST_ID
      }
    ),
    initialPageParam: 0,
    refetchInterval: () => shouldBeLimited() ? undefined : 5000,
    getNextPageParam: CommonService.getNextPageParam
  })
const Body = () => {
  const dispatch = useDispatch(),
        queryClient = useQueryClient(),
        { name, type } = useSelector(state => state.statusesState),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useSearchStatuses({ name, type, getQueryBuilder, shouldBeLimited }),
        onScroll = useCallback(() => fetchNextPage(), [fetchNextPage]),
        scrollCont = useRef()

  useEffect(() => () => {
    queryClient.cancelQueries({ queryKey: [StatusesQuery.KEYS.SEARCH, REQUEST_ID] })
  }, [queryClient])

  return (
    <div 
        className="statuses__history__body"
        ref={scrollCont}
    >
      <When condition={data?.pages?.length === 1 && !isPending && data?.pages?.[0]?.items.length === 0}>
        {() => (
          <NoResults/>
        )}
      </When>
      <When condition={isFetchingNextPage || isPending}>
        <Fetching/>
      </When>
      <InfiniteScroll
          className="statuses__history__body__scroll"
          hasMore={hasNextPage}
          initialLoad={false}
          loadMore={onScroll}
          threshold={800}
          useWindow={false}
      >
        {(data?.pages || []).map((page, pageIndex) => (
          <Fragment key={pageIndex}>
            {page.items.map((status, index) => {
              const datalakeComponent = status.componentStatuses.find(componentStatuses => componentStatuses.component === "Datalake")
              return (
                <Row
                    componentStatuses={status.componentStatuses}
                    fileName={datalakeComponent?.payload?.fileName || "No file name found"}
                    fileType={datalakeComponent?.payload?.fileType || undefined}
                    jobId={status.jobId}
                    key={status.jobId + " " + index}
                    name={status?.type === StatusesEnums.STATUS_TYPE.ETL ? (status?.payload?.groupName || status?.name) : status?.name}
                    payload={status.payload}
                    scrollCont={scrollCont}
                    startTime={status.startTime}
                    status={status.status}
                    type={status.type}
                    version={status.payload?.version}
                    warnStatuses={status.warnStatuses}
                />
              )
            })}
          </Fragment>
        ))}
      </InfiniteScroll>
    </div>
  )
}

export default memo(Body);